/**
 * A tool as the editor holds it, and the lossless way back to what the Worker stores.
 *
 * ALLOWED VALUES ARE A RULE ON A STRING HERE AND A TYPE IN STORAGE, and that is the one
 * translation in this file. Settings v2 lists five types - string, number, integer, boolean,
 * array - and treats "only these values" as a rule you tick on a string, beside Format and
 * Pattern. The Worker has had an `enum` type since 0019, and the model's schema says
 * `{"type": "string", "enum": [...]}` either way. So the editor shows a stored enum as a
 * string with the rule on, and saves a string with the rule on and at least one value as an
 * enum. No migration, and a tool opened and saved without an edit posts what it read.
 *
 * NOTHING HERE VALIDATES WHAT THE WORKER VALIDATES. `validateParams` in `domain/tools.ts`
 * refuses a pattern that does not compile, a range the wrong way round and a rule on the
 * wrong type, and answers with a sentence the editor shows. What this file does is keep the
 * editor from offering a rule the type cannot have, which is a different job.
 */

import type { Connection, ContactKey, ItemType, ParamFormat, Tool, ToolInput, ToolParam } from "../../../api";

/** The editor's five types. `enum` is not one of them; see the header. */
export type EditorType = "string" | "number" | "integer" | "boolean" | "array";
export const EDITOR_TYPES: EditorType[] = ["string", "number", "integer", "boolean", "array"];
export const FORMATS: ParamFormat[] = ["date", "time", "date-time", "email", "uuid"];
export const ITEM_TYPES: ItemType[] = ["string", "number", "integer", "boolean"];

/** A parameter as the editor draws it. `values` non-empty is the Allowed-values rule. */
export interface EditorParam {
  name: string;
  type: EditorType;
  required: boolean;
  description: string;
  values: string[];
  /** Whether the Allowed-values row is ticked, which it can be with nothing typed yet. */
  valuesOn: boolean;
  items: ItemType;
  format?: ParamFormat;
  pattern?: string;
  min?: number;
  max?: number;
  contact?: ContactKey;
}

export function toEditorParam(param: ToolParam): EditorParam {
  const isEnum = param.type === "enum";
  return {
    name: param.name,
    type: isEnum ? "string" : (param.type as EditorType),
    required: param.required,
    description: param.description,
    values: isEnum ? (param.values ?? []) : [],
    valuesOn: isEnum,
    items: param.items ?? "string",
    format: param.format,
    pattern: param.pattern,
    min: param.min,
    max: param.max,
    contact: param.contact,
  };
}

/**
 * Back to the stored shape, with only the keys the type can carry.
 *
 * `items` ONLY ON AN ARRAY, and the rules only where they apply, so a parameter switched
 * from array to string does not carry a stale `items` the Worker would strip anyway - and
 * so a stored tool that never had one does not grow one on its first save.
 */
export function fromEditorParam(param: EditorParam): ToolParam {
  const out: ToolParam = { name: param.name, type: param.type, required: param.required, description: param.description };
  // Only text can be a name, a number or an address; anything can be a detail of its own.
  const textual = param.type === "string" && !(param.valuesOn && param.values.length);
  if (param.contact && (param.contact === "field" || textual)) out.contact = param.contact;
  if (param.type === "string" && param.valuesOn && param.values.length) {
    out.type = "enum";
    out.values = param.values;
    return out;
  }
  if (param.type === "array") out.items = param.items;
  if (param.type === "string") {
    if (param.format) out.format = param.format;
    if (param.pattern) out.pattern = param.pattern;
  }
  if (param.type !== "boolean") {
    if (param.min !== undefined) out.min = param.min;
    if (param.max !== undefined) out.max = param.max;
  }
  return out;
}

/** A new parameter's starting point. */
export function blankParam(): EditorParam {
  return { name: "", type: "string", required: true, description: "", values: [], valuesOn: false, items: "string" };
}

/** A type change keeps the name, the words and Required, and drops every rule. */
export function setType(param: EditorParam, type: EditorType): EditorParam {
  return {
    name: param.name,
    type,
    required: param.required,
    description: param.description,
    values: [],
    valuesOn: false,
    // Where it is filed is not a rule about its shape, so it survives - unless the new type
    // cannot be a name, a number or an address.
    contact: param.contact === "field" || type === "string" ? param.contact : undefined,
    items: param.items,
  };
}

/** What the min/max pair counts, for the row's label and its unit. */
export function rangeWords(type: EditorType): { label: string; unit: string; hint: string } {
  if (type === "string") return { label: "Length", unit: "characters", hint: "Minimum or maximum number of characters" };
  if (type === "array") return { label: "Item count", unit: "items", hint: "Minimum or maximum number of items" };
  return { label: "Range", unit: "", hint: "Lowest or highest allowed number" };
}

/** "Required · 3 choices · up to 80 chars", the line under a parameter in the list. */
export function paramSummary(param: EditorParam): string {
  const parts: string[] = [param.required ? "Required" : "Optional"];
  if (param.type === "array") parts.push(`list of ${param.items}`);
  if (param.valuesOn && param.values.length) {
    parts.push(param.values.length <= 3 ? param.values.join(", ") : `${param.values.length} choices`);
  } else {
    if (param.format) parts.push(param.format);
    if (param.pattern) parts.push("pattern");
  }
  const unit = param.type === "string" ? " chars" : param.type === "array" ? " items" : "";
  if (param.min !== undefined && param.max !== undefined) parts.push(`${param.min} to ${param.max}${unit}`);
  else if (param.min !== undefined) parts.push(`at least ${param.min}${unit}`);
  else if (param.max !== undefined) parts.push(`up to ${param.max}${unit}`);
  return parts.join(" · ");
}

/** A tool's editable half, as the editor holds it. */
export interface EditorTool extends Omit<ToolInput, "params"> {
  params: EditorParam[];
}

export function toEditor(tool: Tool | ToolInput): EditorTool {
  const { params, ...rest } = tool as ToolInput & Partial<Pick<Tool, "id" | "position" | "updatedAt">>;
  const { id: _id, position: _position, updatedAt: _updatedAt, ...input } = rest;
  return { ...input, strict: input.strict ?? false, params: params.map(toEditorParam) };
}

export function fromEditor(tool: EditorTool): ToolInput {
  return { ...tool, params: tool.params.map(fromEditorParam) };
}

/**
 * A new tool: strict, and saved with the call.
 *
 * SAVE FIRST, WHICH REVERSES THE OLD DIALOG. That started every tool as an API request
 * "because that is the one with a form". The prototype starts with Save to the call, and it
 * is the right default for a new owner: it works with nothing else set up, and it is what
 * "take a message" - the first tool nearly everybody builds - actually is.
 */
export function blankTool(): EditorTool {
  return {
    name: "",
    description: "",
    enabled: true,
    strict: true,
    availability: "always",
    kind: "save",
    params: [],
    http: null,
    code: null,
    mapping: [],
    dropUnmapped: true,
    errors: [],
    filler: "",
    fillerAfterMs: 1000,
    onInterrupt: "cancel",
    notifyEmail: "",
  };
}

/** The request half a tool gets when it becomes an API request. */
export function blankHttp(connections: Connection[]): NonNullable<ToolInput["http"]> {
  return {
    connectionId: connections[0]?.id ?? null,
    method: "GET",
    path: "",
    query: [],
    headers: [],
    body: "",
    timeoutMs: 5000,
    retries: 0,
  };
}

/**
 * The code a tool starts with when it becomes "Run code": a working `run` that shows the
 * three things an owner reaches for - the caller's details, the business's, and a request
 * through a connection (left commented until they pick one, so the first test run works).
 * One comment line, because the editor's Insert menu and completions carry the rest.
 */
export function blankCode(params: EditorParam[], connections: Connection[]): string {
  const first = params.find((p) => p.name)?.name;
  const connection = connections[0]?.name;
  return [
    "// What run returns is what the AI hears. Type args. or ctx. to see what you can use.",
    "async function run(args, ctx) {",
    ...(connection
      ? [`  // const res = await ctx.request(${JSON.stringify(connection)}, { path: "/" });`, "  // if (!res.ok) throw new Error(`The system said ${res.status}`);", ""]
      : []),
    `  return { business: ctx.agent.business_name${first ? `, ${first}: args.${first}` : ""} };`,
    "}",
    "",
  ].join("\n");
}

/** Whether two editor states would post the same tool. The dirty check behind Done. */
export function sameTool(a: EditorTool, b: EditorTool): boolean {
  return JSON.stringify(fromEditor(a)) === JSON.stringify(fromEditor(b));
}

/**
 * What the model would send, from the test panel's text boxes.
 *
 * Numbers as numbers, a list as a list, empty as absent - the same coercion the old dialog's
 * panel did, now with the enum folded into string.
 */
export function typedArgs(params: EditorParam[], raw: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const param of params) {
    const value = (raw[param.name] ?? "").trim();
    if (!value) continue;
    if (param.type === "number" || param.type === "integer") out[param.name] = Number(value);
    else if (param.type === "boolean") out[param.name] = value === "true";
    else if (param.type === "array") out[param.name] = value.split(",").map((v) => v.trim()).filter(Boolean);
    else out[param.name] = value;
  }
  return out;
}
